import { Switch } from '@/components';
import { useMessage } from '@/providers';
import { type ComponentProps, type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { useResolveComment } from '../../hooks/comment';
import type { ResolveCommentParamsSchema } from '../../models/comment';

type ResolveCommentParams = z.infer<typeof ResolveCommentParamsSchema>;

type Props = {
  commentId: string;
} & ResolveCommentParams &
  Pick<ComponentProps<typeof Switch>, 'disabled'>;

export const CommentResolveSwitch: FC<Props> = ({
  commentId,
  resolved,
  disabled,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const [isResolveChecked, setIsResolveChecked] = useState(resolved);
  const { mutateAsync: resolveComment } = useResolveComment(commentId);

  useEffect(() => {
    setIsResolveChecked(resolved);
  }, [resolved]);

  return (
    <Switch
      checked={isResolveChecked}
      onChange={async (checked) => {
        setIsResolveChecked(checked);
        try {
          await resolveComment({
            resolved: checked,
          });
          await openMessage('success', t('comment.resolved-item'));
        } catch {
          setIsResolveChecked(!checked);
          await openMessage('error', t('comment.failed-resolve'));
        }
      }}
      checkedLabel={t('resolved')}
      unCheckedLabel={t('unresolved')}
      disabled={disabled}
    />
  );
};
